import React, { useState } from "react";
import { Button, Card, Col, Modal, Row, Spin, Typography } from "antd";
import { useAppSelector } from "../../../store/hooks";
import userSelectors from "../../../store/user/selectors";
import {
  GiftOutlined,
  TrophyOutlined,
  SyncOutlined,
  FrownOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import CardDetail from "./CardDetail";

const ScratchCardList: React.FC = () => {
  const scratchCards = useAppSelector(userSelectors.getScratchCards);
  const loaders = useAppSelector(userSelectors.getLoader);

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);

  const handleViewDetails = (card: any) => {
    setSelectedCard(card);
    setIsDetailModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedCard(null);
  };

  if (loaders)
    return (
      <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'200px'}}>
        <Spin tip="Loading Scratch Cards..." size="large">
          Loading Scratch Cards...
        </Spin>
      </div>
    );

  if (!scratchCards.results.length)
    return (
      <p style={{ textAlign: "center", color: "#000" }}>
        No scratch cards available.
      </p>
    );

  const statusColors = {
    purchased: "linear-gradient(135deg, #fff1e6 0%, #fde4cf 100%)", // playful peach-orange, warm and inviting
    scratched: "linear-gradient(135deg, #e0ebff 0%, #d9f5ff 100%)", // gentle sky blue, curiosity/surprise
    nowinning: "linear-gradient(135deg, #d6dde6 0%, #b8c1d9 100%)", // soft grayish-blue, calm and neutral for no win
    claimed: "linear-gradient(135deg, #eaffd0 0%, #b8f1b0 100%)", // vibrant bright green for winning / claimed
  };

  return (
    <>
      <Row gutter={16}>
        {scratchCards.results.map((item) => {
          let displayStatus = "Reveal Surprize!";
          let displayIcon = (
            <GiftOutlined style={{ fontSize: 32, color: "#faad14" }} />
          );
          let bgColor = statusColors.purchased;

          // Card revealed when scratched_at is set
          if (item.scratched_at) {
            displayStatus = item.is_winner
              ? "Card Scratched!"
              : "Better luck next time!";
            displayIcon = item.is_winner ? (
              <SyncOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
            ) : (
              <FrownOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
            );
            bgColor = item.is_winner
              ? statusColors.scratched
              : statusColors.nowinning;
          }

          // Card claimed when claimed_at is set
          if (item.claimed_at) {
            displayStatus = item.is_winner
              ? "Card Claimed!"
              : "Better luck next time!";
            displayIcon = item.is_winner ? (
              <TrophyOutlined style={{ fontSize: 32, color: "#3a8d00" }} />
            ) : (
              <FrownOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
            );
            bgColor = item.is_winner
              ? statusColors.claimed
              : statusColors.nowinning;
          }

          return (
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={6}
              xl={6}
              style={{ marginBottom: 16 }}
              key={item.id}
            >
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  background: bgColor,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  minHeight: 180,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  disabled={!item.is_purchase_sucessfull}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#333",
                  }}
                  onClick={() => handleViewDetails(item)}
                />
                <div style={{ textAlign: "center", marginBottom: 12 }}>
                  {displayIcon}
                  <div
                    style={{
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      marginTop: 4,
                      color: item.is_winner ? "#3a8d00" : "#333",
                    }}
                  >
                    {displayStatus}
                  </div>
                  {item.scratched_at && (
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: item.is_winner ? "#44b700" : "#888",
                        marginTop: 8,
                      }}
                    >
                      {item.is_winner ? (
                        <>
                          You Won: {Number(item.win_amount).toFixed(2)}
                          <sub className="sub-script-text">HVT</sub>
                        </>
                      ) : (
                        <>No Winning!</>
                      )}
                    </div>
                  )}
                </div>
                {!item.scratched_at && (
                  <Button
                    type="primary"
                    disabled={!item.is_purchase_sucessfull}
                    style={{
                      borderRadius: 8,
                      fontSize: "1rem",
                      marginTop: 10,
                      background:
                        "linear-gradient(90deg, #faad14 0%, #f0e130 100%)",
                      boxShadow: "0 0 8px #faad1444",
                    }}
                    onClick={() => handleViewDetails(item)}
                  >
                    🎉 Scratch Card
                  </Button>
                )}
                {!item.is_purchase_sucessfull && (
                  <div
                    style={{
                      marginTop: 10,
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    <Typography.Text>Transaction in process...</Typography.Text>
                  </div>
                )}
                {item.is_winner && item.scratched_at && !item.claimed_at && (
                  <div
                    style={{
                      marginTop: 10,
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    <Typography.Text>Transaction in process...</Typography.Text>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
      {/* Detail Modal */}
      <Modal
        visible={isDetailModalOpen}
        title="Card Details"
        footer={null}
        centered
        onCancel={handleCloseModal}
        width={500}
      >
        <CardDetail card={selectedCard} closeModal={handleCloseModal} />
      </Modal>
    </>
  );
};

export default ScratchCardList;
